import { el } from '../dom.js';
import {
  startScoreCounter, stopScoreCounter, playCategoryLock, playScoreTotal,
} from '../missionEndedSound.js';

/**
 * End-of-mission score sheet (combat scenarios only).
 *
 * A full-screen tactical readout shown once a combat mission ends — either by
 * clearing every enemy or by losing the aircraft. Driven by App via
 * {@link render}, which rebuilds the sheet from a {@link MissionStats} snapshot;
 * a single "Return to Start" button (also bound to R / Enter by App) takes the
 * player back to the start screen.
 */
export default class MissionEndedScreen {
  constructor({ onReturn }) {
    this.onReturn = onReturn;

    this.el = el('div', 'screen mission-ended-screen is-hidden');
    this._inner = el('div', 'ended-inner');
    this.el.appendChild(this._inner);

    this._rafId = null; // active score count-up animation handle
    this._revealId = null; // pending grade-reveal timeout handle
  }

  setVisible(v) {
    if (!v) this._cancelScoreCount();
    this.el.classList.toggle('is-hidden', !v);
  }

  /**
   * Rebuild the sheet from a MissionStats snapshot.
   * @param {import('../../mission/MissionStats.js').default} stats
   * @param {'complete' | 'failed'} outcome
   */
  render(stats, outcome) {
    const bd = stats.computeBreakdown();
    const acc = stats.accuracyStrings;
    this._inner.replaceChildren();

    // ── Header ──
    const head = el('div', 'ended-head');
    const title = el('div', 'ended-title', outcome === 'complete' ? 'MISSION COMPLETE' : 'MISSION FAILED');
    title.classList.toggle('is-failed', outcome !== 'complete');
    head.appendChild(title);
    head.appendChild(el('div', 'ended-subtitle',
      outcome === 'complete' ? 'ALL ENEMIES DESTROYED' : 'AIRCRAFT DESTROYED'));
    this._inner.appendChild(head);

    // ── Row 1: score card + score breakdown ──
    const breakdown = el('div', 'ended-breakdown');
    breakdown.appendChild(el('div', 'ended-card-title', 'Evaluation'));
    const cols = el('div', 'ended-bd-cols');
    // One column per scored category. (The category rollup lives in the score
    // card; these columns carry the per-category line items + subtotals.)
    for (const g of bd.groups) {
      cols.appendChild(this._breakdownCol(g.title, g.lines, g.total));
    }
    breakdown.appendChild(cols);

    const mainRow = el('div', 'ended-row-main');
    mainRow.appendChild(this._scoreCard(bd));
    mainRow.appendChild(breakdown);
    this._inner.appendChild(mainRow);

    // ── Section break ──
    this._inner.appendChild(el('div', 'ended-section-break', 'STATS'));

    // ── Row 2: the four stat cards ──
    const cards = el('div', 'ended-cards');

    cards.appendChild(this._statCard('Munitions', [
      ['30mm Rounds Fired', fmtNum(stats.gunFired)],
      ['Mavericks Fired', fmtNum(stats.mavericksFired)],
      ['Sidewinders Fired', fmtNum(stats.sidewindersFired)],
      ['Flares Deployed', fmtNum(stats.flaresDeployed)],
    ], '/assets/ui/icon_bullet.png'));

    cards.appendChild(this._statCard('Accuracy', [
      ['30mm Accuracy', acc.gun],
      ['Maverick Accuracy', acc.maverick],
      ['Sidewinder Accuracy', acc.sidewinder],
    ], '/assets/ui/icon_focus.png'));

    const killRows = [
      ['Total Enemies Destroyed', fmtNum(stats.totalKills)],
      ['Ground Vehicles', fmtNum(stats.groundKills)],
      ['Aircraft Destroyed', fmtNum(stats.aircraftKills)],
    ];
    const perType = stats.killRows;
    if (perType.length) {
      killRows.push('divider');
      for (const r of perType) killRows.push([r.label, fmtNum(r.count)]);
    }
    cards.appendChild(this._statCard('Enemy Kills', killRows, '/assets/ui/icon_skull.png'));

    const survCard = this._statCard('Survivability', [
      ['Enemy Missiles Evaded', fmtNum(stats.missilesEvaded)],
      'divider',
    ], '/assets/ui/icon_shield.png');
    // Damage Taken reads as a stacked label + emphasised state (the value can be
    // as long as "Both Engines Lost").
    const dmg = el('div', 'ended-damage');
    dmg.appendChild(el('div', 'ended-damage-label', 'Damage Taken'));
    dmg.appendChild(el('div', `ended-damage-value ${damageClass(stats.damageState)}`,
      DAMAGE_LABEL[stats.damageState]));
    survCard.appendChild(dmg);
    cards.appendChild(survCard);

    this._inner.appendChild(cards);

    // ── Footer ──
    const footer = el('div', 'ended-footer');
    const btn = el('button', 'ended-btn', 'Return to Start');
    btn.type = 'button';
    btn.addEventListener('click', () => this.onReturn?.());
    footer.appendChild(btn);
    this._inner.appendChild(footer);
  }

  // ── Card builders ───────────────────────────────────────────────────────
  // The Score card hosts the category rollup (no total row) with the total
  // score + grade pinned, oversized, to the bottom. The values start at zero
  // and animate in via _startScoreCount().
  _scoreCard(bd) {
    const card = el('div', 'ended-card ended-card--score');
    card.appendChild(el('div', 'ended-card-title', 'Score'));

    const rollup = el('div', 'ended-score-rollup');
    const valueEls = [];
    for (const line of bd.summary) {
      const row = el('div', 'ended-bd-row');
      row.appendChild(el('span', 'ended-bd-label', line.label));
      // Sign-colored from the final value; the running figure stays that sign.
      const v = el('span', `ended-bd-value ${signClass(line.value)}`, fmtSigned(0));
      row.appendChild(v);
      valueEls.push(v);
      rollup.appendChild(row);
    }
    card.appendChild(rollup);

    const totals = el('div', 'ended-score-totals');
    totals.appendChild(el('div', 'ended-score-label', 'Total Score'));
    const totalEl = el('div', 'ended-score-value', fmtNum(0));
    totals.appendChild(totalEl);
    // Grade carries its final value but stays hidden until the total finishes
    // counting, then bounces in (see _startScoreCount → finish()). The letter
    // also lands in the "GRADE:" label (bold), with the badge image below it.
    const gradeWrap = el('div', 'ended-grade');
    const gradeLabel = el('div', 'ended-score-label');
    gradeLabel.append('Grade: ', el('span', 'ended-grade-letter', bd.grade));
    gradeWrap.appendChild(gradeLabel);
    const gradeImg = el('img', 'ended-grade-img');
    gradeImg.src = `/assets/ui/grade_${bd.grade.toLowerCase()}.png`;
    gradeImg.alt = `Grade ${bd.grade}`;
    gradeWrap.appendChild(gradeImg);
    totals.appendChild(gradeWrap);
    card.appendChild(totals);

    this._startScoreCount(bd.summary, valueEls, totalEl, gradeWrap);
    return card;
  }

  // Count each category value up from zero, one at a time, with the total score
  // tracking the running sum. The whole sequence spans COUNT_MS; zero-value
  // categories are instantaneous so they neither stall the run nor visibly tick.
  // Each value flashes (scale + glow) the instant it locks; the total flashes
  // and the grade reveals + flashes once the count has fully landed.
  _startScoreCount(summary, valueEls, totalEl, gradeWrap) {
    this._cancelScoreCount();

    const COUNT_MS = 4000;
    const REVEAL_DELAY_MS = 850; // beat held after the last category locks before the grade reveals
    const grand = summary.reduce((a, c) => a + c.value, 0);
    const nonZero = summary.reduce((n, c) => n + (c.value !== 0 ? 1 : 0), 0);

    // The count-up has landed: silence the running loop and settle the total.
    // After a short beat, ring out the total and bounce the grade badge in.
    const land = () => {
      stopScoreCounter();
      totalEl.textContent = fmtNum(grand);
      this._revealId = setTimeout(() => {
        this._revealId = null;
        playScoreTotal();     // ring out the final total
        pop(totalEl);
        gradeWrap.classList.add('is-shown'); // bounce the grade badge in
      }, REVEAL_DELAY_MS);
    };
    if (nonZero === 0) { land(); return; } // scoreless run — nothing to animate

    // Per-category time windows on a shared timeline; only non-zero categories
    // consume a slice.
    const slice = COUNT_MS / nonZero;
    const segs = [];
    let acc = 0;
    for (const c of summary) {
      const dur = c.value !== 0 ? slice : 0;
      segs.push({ start: acc, dur, to: c.value, flashed: false });
      acc += dur;
    }
    const totalMs = acc;

    startScoreCounter(); // loop runs for the whole count-up; finish() stops it

    let startTs = 0;
    const tick = (ts) => {
      if (!startTs) startTs = ts;
      const elapsed = ts - startTs;
      const done = elapsed >= totalMs;

      let totalRaw = 0;
      for (let i = 0; i < segs.length; i++) {
        const s = segs[i];
        let v;
        if (s.dur === 0 || elapsed >= s.start + s.dur) {
          v = s.to;                                                  // done / instant
          // Flash the value + tick the category sound the frame it locks (skip
          // never-counted zeros — they neither flash nor sound).
          if (s.dur > 0 && !s.flashed) { s.flashed = true; pop(valueEls[i]); playCategoryLock(); }
        } else if (elapsed <= s.start) {
          v = 0;                                                     // not yet started
        } else {
          v = s.to * ((elapsed - s.start) / s.dur);                 // counting
        }
        totalRaw += v;
        valueEls[i].textContent = fmtSigned(Math.round(v));
      }
      totalEl.textContent = fmtNum(done ? grand : Math.round(totalRaw));

      if (done) { this._rafId = null; land(); }
      else this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  _cancelScoreCount() {
    if (this._rafId != null) { cancelAnimationFrame(this._rafId); this._rafId = null; }
    if (this._revealId != null) { clearTimeout(this._revealId); this._revealId = null; }
    stopScoreCounter(); // never leave the loop running past the count-up
  }

  // rows: array of [label, value, valueClass?] tuples, or the string 'divider'.
  // `icon` is an optional asset path rendered to the left of the title.
  _statCard(title, rows, icon) {
    const card = el('div', 'ended-card');
    const titleEl = el('div', 'ended-card-title ended-card-title--icon');
    if (icon) {
      const img = el('img', 'ended-card-icon');
      img.src = icon;
      img.alt = '';
      titleEl.appendChild(img);
    }
    titleEl.appendChild(el('span', null, title));
    card.appendChild(titleEl);
    for (const row of rows) {
      if (row === 'divider') { card.appendChild(el('div', 'ended-divider')); continue; }
      const [label, value, valueClass] = row;
      const r = el('div', 'ended-stat');
      r.appendChild(el('span', 'ended-stat-label', label));
      r.appendChild(el('span', `ended-stat-value${valueClass ? ` ${valueClass}` : ''}`, value));
      card.appendChild(r);
    }
    return card;
  }

  // lines: array of { label, value } scored line-items. `totalLabel` overrides
  // the default "Total".
  _breakdownCol(title, lines, total, totalLabel = 'Total') {
    const col = el('div', 'ended-bd-col');
    col.appendChild(el('div', 'ended-bd-title', title));
    for (const line of lines) {
      const row = el('div', 'ended-bd-row');
      row.appendChild(el('span', 'ended-bd-label', line.label));
      row.appendChild(el('span', `ended-bd-value ${signClass(line.value)}`, fmtSigned(line.value)));
      col.appendChild(row);
    }
    const totalRow = el('div', 'ended-bd-row ended-bd-total');
    totalRow.appendChild(el('span', 'ended-bd-label', totalLabel));
    totalRow.appendChild(el('span', `ended-bd-value ${signClass(total)}`, fmtSigned(total)));
    col.appendChild(totalRow);
    return col;
  }
}

const DAMAGE_LABEL = {
  none: 'None',
  one:  'One Engine Lost',
  both: 'Both Engines Lost',
};

function damageClass(state) {
  return state === 'none' ? 'is-pos' : 'is-neg';
}

// Fire the "lock-in" flash on a value element, restarting the CSS animation
// from the top each call (remove → reflow → re-add).
function pop(elem) {
  if (!elem) return;
  elem.classList.remove('ended-pop');
  void elem.offsetWidth; // force reflow so the animation replays
  elem.classList.add('ended-pop');
}

// Thousands-separated integer (e.g. 15450 → "15,450"). Handles negatives.
function fmtNum(n) {
  return Math.round(n).toLocaleString('en-US');
}

// Signed, thousands-separated (e.g. +5000 → "+5,000", -300 → "-300", 0 → "+0").
function fmtSigned(n) {
  const sign = n < 0 ? '-' : '+';
  return `${sign}${fmtNum(Math.abs(n))}`;
}

function signClass(n) {
  if (n > 0) return 'is-pos';
  if (n < 0) return 'is-neg';
  return 'is-zero';
}
